import { SelectItem } from "components/molecules/MultiSelectFreeSolo";
import TagsSidebar, { SidebarTagProps } from "components/templates/TagsSidebar";
import {
  GetMappingAssetTagsQuery,
  PaginatedResultType,
} from "graphqlBase/MappingAssetTag/__generated__/getMappingAssetTags";
import { TagCreateType } from "graphqlBase/types";
import getTestIDs from "lib/utils/getTestIDs";
import React from "react";
import { useRecoilState, useSetRecoilState } from "recoil";
import useHandleUpdateAssetTags from "./hooks/useHandleUpdateAssetTags";
import { assetTagIdsToDeleteAtom, assetTagsforTagsListAtom, assetTagsToCreateAtom } from "./recoilState/assetTagsState";
import { assignedAssetTagErrorAtom } from "./recoilState/tagModalState";
import translations from "translations";

export const testIDs = getTestIDs();

export type MappingAssetTag = PaginatedResultType<GetMappingAssetTagsQuery["mappingAssetTags"]>;

const AssetTagsSidebar: React.FC<{
  afterMutation: () => Promise<void>;
  loading: boolean;
  onClose: () => void;
  open: boolean;
}> = ({ open = false, onClose, afterMutation, loading }) => {
  const [assetTagsForTagsList, setAssetTagsForTagsList] = useRecoilState(assetTagsforTagsListAtom);
  const [assignedTagError, setAssignedTagError] = useRecoilState(assignedAssetTagErrorAtom);
  const setCreateAssetTags = useSetRecoilState(assetTagsToCreateAtom);
  const setTagIdsToDelete = useSetRecoilState(assetTagIdsToDeleteAtom);

  const { handleAddAndDelete, tagModalLoading, assetTagsData, handleCloseTagsModal } = useHandleUpdateAssetTags({
    afterMutation,
    onClose,
    loading,
    open,
  });

  const tagOptions =
    assetTagsData?.assetTags?.items?.reduce<SelectItem[]>((tagOptions, current) => {
      return tagOptions.concat({ label: current.tagValue, value: current.id });
    }, []) ?? [];

  const handleCloseToast = () => {
    setAssignedTagError(undefined);
  };

  const compareIgnoreCase = (str1: string, str2: string) => str1.toLowerCase() === str2.toLowerCase();

  const createTagsCB = (value: SidebarTagProps[]) => {
    const result = value.reduce((acc: TagCreateType[], curr) => {
      const existingTags = tagOptions?.filter((assetTag) => compareIgnoreCase(assetTag.label, curr?.label ?? ""));
      const labelValue: string = typeof curr?.label === "string" ? (curr.label as string) : "";

      // Create tag if it's new (no ID from database) and doesn't already exist in options
      if (!existingTags?.length && labelValue.trim()) {
        const tagValue = labelValue;
        return acc.concat({ tagValue });
      }
      return acc;
    }, []);
    setCreateAssetTags(result);
  };

  const handleAddTag = (tag: SidebarTagProps) => {
    const newAssetTagsForTagsList = [...(assetTagsForTagsList ?? []), { ...tag, isNewMapped: true }];
    setAssetTagsForTagsList(newAssetTagsForTagsList);
  };

  const handleCreateTag = (tag: string) => {
    if (tag?.trim()?.length) {
      const compareTagLabel = (existingTag: SidebarTagProps) => {
        return compareIgnoreCase(tag, existingTag?.label ?? "");
      };

      const existingTagInList = (assetTagsForTagsList ?? []).find(compareTagLabel);

      if (!existingTagInList) {
        const tagObject: SidebarTagProps = {
          label: tag,
          isNewMapped: true,
        };
        const newAssetTagsForTagsList = [...(assetTagsForTagsList ?? []), tagObject];
        setAssetTagsForTagsList(newAssetTagsForTagsList);
        createTagsCB(newAssetTagsForTagsList);
      }
    }
  };

  const handleDeleteTag = (item: SidebarTagProps) => {
    if (tagModalLoading) return;
    const tagToRemove = (assetTagsForTagsList ?? []).find((tag) => {
      return tag.label === item.label;
    });

    if (tagToRemove) {
      setAssetTagsForTagsList((prevTags) => (prevTags ?? []).filter((tag) => tag !== tagToRemove));
      // Check if tag has an ID (existing tag from database)
      const tagValue = tagToRemove.id;
      if (tagValue !== undefined) {
        setTagIdsToDelete((prev) => [...prev, tagValue ?? ""]);
      }
    }
  };

  return (
    <TagsSidebar
      handleCloseToast={handleCloseToast}
      selectedTags={assetTagsForTagsList ?? []}
      options={tagOptions}
      handleCancel={handleCloseTagsModal}
      handleSave={handleAddAndDelete}
      handleDeleteTag={handleDeleteTag}
      handleAddTag={handleAddTag}
      handleCreateTag={handleCreateTag}
      open={open}
      disabled={tagModalLoading}
      loading={tagModalLoading}
      title={translations.pages.assetManagement.assetTags.updateTags}
      error={assignedTagError}
    />
  );
};

export default AssetTagsSidebar;
